import type { ReactNode } from 'react'
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowUpDown, ArrowUp, ArrowDown, Columns3, Download, Check, FileSpreadsheet,
  MoreVertical,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { DropdownMenu, type DropdownItem } from './DropdownMenu'

export interface DataTableColumn<T> {
  key: string
  header: string
  render?: (item: T) => ReactNode
  className?: string
  sortable?: boolean
  /** key real do backend a usar em `?sort=` (se diferente da `key`) */
  sortKey?: string
  /**
   * Quando `true`, esta coluna é usada como TÍTULO do card mobile.
   * Default: a primeira coluna serve de título se nada for marcado.
   */
  mobileTitle?: boolean
  /** Quando `true`, esta coluna NÃO aparece no card mobile. */
  hideOnMobile?: boolean
  /** Quando `true`, a coluna é fixa (não pode ser ocultada pelo seletor). */
  alwaysVisible?: boolean
  /** Quando `true`, a coluna começa oculta — usuário precisa habilitá-la. */
  hiddenByDefault?: boolean
  /** Alinhamento horizontal do conteúdo da coluna. Default: 'left'. */
  align?: 'left' | 'center' | 'right'
}

/** Itens do menu de ação para uma linha — substitui botões inline. */
export type RowActionsProvider<T> = (row: T) => DropdownItem[]

interface Props<T> {
  /**
   * Identificador único da tabela — usado para persistir preferências
   * (visibilidade de colunas) em localStorage. Sem ele as preferências NÃO
   * são salvas e o seletor de colunas é ocultado.
   */
  tableId?: string
  columns: DataTableColumn<T>[]
  rows: T[]
  loading?: boolean
  emptyMessage?: string
  getRowKey?: (row: T) => string
  onRowClick?: (row: T) => void
  sort?: { field: string | null; direction: 'asc' | 'desc' }
  onSortChange?: (next: { field: string | null; direction: 'asc' | 'desc' }) => void
  /** Menu dropdown de ações por linha (regra global: ações em dropdown). */
  rowActions?: RowActionsProvider<T>
  /** Habilita botão "Exportar Excel" no toolbar interno. */
  exportable?: boolean
  /** Chamado quando usuário escolhe "Exportar página atual". */
  onExportPage?: () => void
  /** Chamado quando usuário escolhe "Exportar todos os registros" (respeitando filtros). */
  onExportAll?: () => void
  /**
   * Detalhe da linha aberto logo abaixo dela, ocupando a largura toda.
   * A página controla o que está aberto: devolva `null` para a linha fechada.
   */
  renderExpanded?: (row: T) => ReactNode | null
}

/**
 * Tabela responsiva moderna com:
 *  - Toolbar interno (seletor de colunas + exportar Excel)
 *  - Ações por linha em dropdown (kebab) com suporte a `danger`
 *  - Visibilidade de colunas persistida em localStorage (por `tableId`)
 *  - Sticky header em desktop
 *  - Cards empilhados em mobile (<md)
 */
export function DataTable<T>({
  tableId, columns, rows, loading, emptyMessage = 'Nenhum registro encontrado.',
  getRowKey, onRowClick, sort, onSortChange, rowActions,
  exportable, onExportPage, onExportAll, renderExpanded,
}: Props<T>) {

  // ---- Estado de visibilidade de colunas (persistido) ---------------------
  const storageKey = tableId ? `dt:cols:${tableId}` : null
  const initialHidden = useMemo(() => {
    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey)
        if (raw) return new Set<string>(JSON.parse(raw) as string[])
      } catch { /* ignore */ }
    }
    return new Set(columns.filter((c) => c.hiddenByDefault).map((c) => c.key))
  }, [storageKey, columns])
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(initialHidden)

  useEffect(() => {
    if (!storageKey) return
    try { localStorage.setItem(storageKey, JSON.stringify(Array.from(hiddenCols))) } catch { /* ignore */ }
  }, [hiddenCols, storageKey])

  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenCols.has(c.key)),
    [columns, hiddenCols],
  )

  // ---- Sort ---------------------------------------------------------------
  function handleHeaderClick(col: DataTableColumn<T>) {
    if (!col.sortable || !onSortChange) return
    const field = col.sortKey ?? col.key
    if (sort?.field !== field) onSortChange({ field, direction: 'asc' })
    else if (sort.direction === 'asc') onSortChange({ field, direction: 'desc' })
    else onSortChange({ field: null, direction: 'asc' })
  }

  // ---- Mobile (cards) -----------------------------------------------------
  const titleCol = visibleColumns.find((c) => c.mobileTitle) ?? visibleColumns[0]
  const visibleMobileCols = visibleColumns.filter((c) => c !== titleCol && !c.hideOnMobile)

  // ---- Render -------------------------------------------------------------
  const showToolbar = exportable || (storageKey && columns.length > 1)
  const showActions = !!rowActions

  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 overflow-hidden">
      {showToolbar && (
        <div className="flex items-center justify-end gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/40">
          {storageKey && columns.length > 1 && (
            <ColumnPicker columns={columns} hidden={hiddenCols} onChange={setHiddenCols} />
          )}
          {exportable && (onExportPage || onExportAll) && (
            <ExportMenu onExportPage={onExportPage} onExportAll={onExportAll} />
          )}
        </div>
      )}

      {/* ===== Tabela (≥md) ===== */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50/80 sticky top-0 z-[1]">
            <tr>
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => handleHeaderClick(c)}
                  className={cn(
                    'px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200',
                    c.align === 'center' ? 'text-center' : c.align === 'right' ? 'text-right' : 'text-left',
                    c.sortable && 'cursor-pointer select-none hover:text-gray-700',
                    c.className,
                  )}
                >
                  <div className={cn(
                    'inline-flex items-center gap-1',
                    c.align === 'center' ? 'justify-center' : c.align === 'right' ? 'justify-end' : '',
                  )}>
                    {c.header}
                    {c.sortable && (
                      sort?.field === (c.sortKey ?? c.key)
                        ? (sort.direction === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
                        : <ArrowUpDown size={11} className="opacity-40" />
                    )}
                  </div>
                </th>
              ))}
              {showActions && <th className="px-2 py-3 w-12 border-b border-gray-200" aria-label="Ações" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={visibleColumns.length + (showActions ? 1 : 0)} className="px-4 py-10 text-center text-gray-400">Carregando</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + (showActions ? 1 : 0)} className="px-4 py-10 text-center text-gray-400">{emptyMessage}</td></tr>
            ) : (
              rows.map((row, i) => {
                const detail = renderExpanded?.(row) ?? null
                return (
                <Fragment key={getRowKey ? getRowKey(row) : i}>
                <tr
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn('hover:bg-brand-50/30 transition', onRowClick && 'cursor-pointer', detail != null && 'bg-brand-50/30')}
                >
                  {visibleColumns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'px-4 py-3 text-gray-700 align-middle',
                        c.align === 'center' ? 'text-center' : c.align === 'right' ? 'text-right' : 'text-left',
                        c.className,
                      )}
                    >
                      {c.render ? c.render(row) : (row as Record<string, unknown>)[c.key] as ReactNode ?? '—'}
                    </td>
                  ))}
                  {showActions && (
                    <td className="px-2 py-2 align-middle text-right" onClick={(e) => e.stopPropagation()}>
                      <RowActions items={rowActions!(row)} />
                    </td>
                  )}
                </tr>
                {detail != null && (
                  <tr className="bg-gray-50/60">
                    <td colSpan={visibleColumns.length + (showActions ? 1 : 0)} className="px-4 py-3">{detail}</td>
                  </tr>
                )}
                </Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ===== Cards (<md) ===== */}
      <div className="md:hidden divide-y divide-gray-100">
        {loading ? (
          <div className="px-4 py-10 text-center text-gray-400">Carregando</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-gray-400">{emptyMessage}</div>
        ) : (
          rows.map((row, i) => (
            <div
              key={getRowKey ? getRowKey(row) : i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn('px-4 py-3.5 active:bg-gray-50 transition relative', onRowClick && 'cursor-pointer')}
            >
              <div className="flex items-start justify-between gap-2">
                {titleCol && (
                  <div className="text-[13px] mb-2 flex-1 min-w-0">
                    {titleCol.render ? titleCol.render(row) : (row as Record<string, unknown>)[titleCol.key] as ReactNode}
                  </div>
                )}
                {showActions && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <RowActions items={rowActions!(row)} />
                  </div>
                )}
              </div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
                {visibleMobileCols.map((c) => {
                  const value = c.render ? c.render(row) : (row as Record<string, unknown>)[c.key] as ReactNode
                  if (value === null || value === undefined || value === '—' || value === '') return null
                  return (
                    <div key={c.key} className="contents">
                      <dt className="text-gray-500 uppercase text-[10px] tracking-wider pt-1">{c.header}</dt>
                      <dd className="text-gray-700">{value ?? '—'}</dd>
                    </div>
                  )
                })}
              </dl>
              {renderExpanded?.(row) != null && <div className="mt-3">{renderExpanded(row)}</div>}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Subcomponentes
// =============================================================================

function RowActions({ items }: { items: DropdownItem[] }) {
  if (!items || items.length === 0) return null
  return <DropdownMenu items={items} ariaLabel="Ações da linha" trigger={<MoreVertical size={16} />} />
}

function ColumnPicker<T>({
  columns, hidden, onChange,
}: {
  columns: DataTableColumn<T>[]
  hidden: Set<string>
  onChange: (next: Set<string>) => void
}) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const recalc = () => {
    const btn = btnRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const panelWidth = panelRef.current?.offsetWidth ?? 240
    setPos({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - panelWidth),
    })
  }
  useLayoutEffect(() => {
    if (!open) return
    recalc()
    const t = requestAnimationFrame(recalc)
    return () => cancelAnimationFrame(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onScroll = () => setOpen(false)
    const onResize = () => recalc()
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function toggle(key: string) {
    const next = new Set(hidden)
    if (next.has(key)) next.delete(key); else next.add(key)
    onChange(next)
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'cursor-pointer inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[12px] font-medium ring-1 ring-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition',
          open && 'bg-gray-50',
        )}
      >
        <Columns3 size={14} /> Colunas
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="min-w-[240px] bg-white ring-1 ring-gray-200 rounded-md py-1 max-h-[60vh] overflow-y-auto"
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-400 font-semibold border-b border-gray-100">
            Colunas visíveis
          </div>
          {columns.map((c) => {
            const isHidden = hidden.has(c.key)
            const isLocked = c.alwaysVisible
            return (
              <button
                key={c.key}
                type="button"
                disabled={isLocked}
                onClick={() => toggle(c.key)}
                className={cn(
                  'cursor-pointer w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] hover:bg-gray-50 text-gray-700',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                <span>{c.header || c.key}</span>
                <span className={cn(
                  'w-4 h-4 rounded border flex items-center justify-center transition',
                  !isHidden ? 'bg-brand-500 border-brand-500 text-white' : 'border-gray-300 bg-white',
                )}>
                  {!isHidden && <Check size={11} strokeWidth={3} />}
                </span>
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </>
  )
}

function ExportMenu({
  onExportPage, onExportAll,
}: {
  onExportPage?: () => void
  onExportAll?: () => void
}) {
  const items: DropdownItem[] = []
  if (onExportPage) items.push({ key: 'page', label: 'Página atual',                       icon: <FileSpreadsheet size={14} />, onClick: onExportPage })
  if (onExportAll)  items.push({ key: 'all',  label: 'Todos os registros (com filtros)',   icon: <FileSpreadsheet size={14} />, onClick: onExportAll })

  return (
    <DropdownMenu
      items={items}
      ariaLabel="Exportar Excel"
      trigger={<span className="inline-flex items-center gap-1.5 text-[12px] font-medium px-1"><Download size={14} /> Exportar</span>}
      buttonClassName="!w-auto px-2.5 h-8 ring-1 ring-gray-200 bg-white hover:bg-gray-50 text-gray-600"
    />
  )
}
